import type { AdminSurface } from '@/lib/guards';

const ROOT: Record<AdminSurface, string> = {
  platform: '/admin/platform',
  workspace: '/admin/workspace',
  clinical: '/app',
};

export interface SurfacePaths {
  root: string;
  clients: {
    list: string;
    detail: (id: string) => string;
    edit: (id: string) => string;
    exerciseReview: (id: string, clientExerciseId: string) => string;
  };
  sessions: {
    list: string;
    detail: (id: string) => string;
    note: (id: string) => string;
    caseReport: (id: string, reportId: string) => string;
    caseReportNew: (id: string) => string;
    book: string;
  };
  practitioners: {
    list: string;
    detail: (id: string) => string;
    edit: (id: string) => string;
  };
  assessments: {
    list: string;
    new: string;
    template: (id: string) => string;
    templateAssign: (id: string) => string;
    templateBulkAssign: (id: string) => string;
    assignmentsList: string;
    assignment: (id: string) => string;
  };
  redFlags: { list: string; detail: (id: string) => string };
  exercises: {
    list: string;
    detail: (id: string) => string;
    new: string;
    edit: (id: string) => string;
  };
  engagementResources: {
    list: string;
    detail: (id: string) => string;
    new: string;
    edit: (id: string) => string;
  };
}

export function surfacePaths(surface: AdminSurface): SurfacePaths {
  const root = ROOT[surface];
  return {
    root,
    clients: {
      list: `${root}/clients`,
      detail: (id) => `${root}/clients/${id}`,
      edit: (id) => `${root}/clients/${id}/edit`,
      exerciseReview: (id, ce) => `${root}/clients/${id}/exercises/${ce}`,
    },
    sessions: {
      list: `${root}/sessions`,
      detail: (id) => `${root}/sessions/${id}`,
      note: (id) => `${root}/sessions/${id}/note`,
      caseReport: (id, reportId) => `${root}/sessions/${id}/case-reports/${reportId}`,
      caseReportNew: (id) => `${root}/sessions/${id}/case-reports/new`,
      book: `${root}/sessions/book`,
    },
    practitioners: {
      list: `${root}/practitioners`,
      detail: (id) => `${root}/practitioners/${id}`,
      // On the clinical surface the only editable practitioner is the logged-in
      // user themselves; the screen lives at a static `/app/profile/edit` URL,
      // not under `/practitioners/:id`. Route back to that instead of emitting
      // a 404 link when `PractitionerDetailScreen` renders its Edit action.
      edit: (id) =>
        surface === 'clinical' ? `${root}/profile/edit` : `${root}/practitioners/${id}/edit`,
    },
    assessments: {
      list: `${root}/assessments`,
      new: `${root}/assessments/new`,
      template: (id) => `${root}/assessments/${id}`,
      templateAssign: (id) => `${root}/assessments/${id}/assign`,
      templateBulkAssign: (id) => `${root}/assessments/${id}/bulk-assign`,
      assignmentsList: `${root}/assessments/assignments`,
      assignment: (id) => `${root}/assessments/assignments/${id}`,
    },
    redFlags: {
      list: `${root}/red-flags`,
      detail: (id) => `${root}/red-flags/${id}`,
    },
    exercises: {
      list: `${root}/exercises`,
      detail: (id) => `${root}/exercises/${id}`,
      new: `${root}/exercises/new`,
      edit: (id) => `${root}/exercises/${id}/edit`,
    },
    engagementResources: {
      list: `${root}/engagement/resources`,
      detail: (id) => `${root}/engagement/resources/${id}`,
      new: `${root}/engagement/resources/new`,
      edit: (id) => `${root}/engagement/resources/${id}/edit`,
    },
  };
}
